import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { combineLatestWith, filter, withLatestFrom } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { GlobalService } from '../../common/services/global.service';
import { BehaviorSubject } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class PiwikService { 
    /**
     * Whether javascript code for piwik has been downloaded and executed
     */
    private piwikLoaded = new BehaviorSubject(false);

    constructor(
        private router: Router,
        @Inject(PLATFORM_ID) private platformId: Object,
        private globalService: GlobalService
    ) {}

    /**
     * Initialize piwik service
     */
    initialize(): void {
        if (isPlatformBrowser(this.platformId)) {
            this.loadPiwikScript();
            this.setupRouterEvents();
        }
    }

    /**
     * Loading piwik script for integration
     * @returns
     */
    private loadPiwikScript(): void {
        const piwikSiteId = this.globalService.piwikConfig.value?.site_id;
        const piwikAccountName = this.globalService.piwikConfig.value?.account_name;

        if (!piwikSiteId || !piwikAccountName) {
            console.warn('Piwik configuration missing');
            return;
        }

        let piwikScript = document.createElement('script');
        piwikScript.setAttribute('defer', 'true');
        piwikScript.onload = () => {
            this.piwikLoaded.next(true);
        }
        // Create the script URL instead of using innerHTML
        piwikScript.src = `https://${piwikAccountName}.containers.piwik.pro/${piwikSiteId}.js`;
        
        // Append to head or body (more standard approach)
        document.head.appendChild(piwikScript);
        
        // If you need the additional code for configuration:
        const configScript = document.createElement('script');
        configScript.textContent = `
            (function(window, document, dataLayerName, id) {
            window[dataLayerName]=window[dataLayerName]||[],window[dataLayerName].push({start:(new Date).getTime(),event:"stg.start"});
            function stgCreateCookie(a,b,c){var d="";if(c){var e=new Date;e.setTime(e.getTime()+24*c*60*60*1e3),d="; expires="+e.toUTCString()}document.cookie=a+"="+b+d+"; path=/"}
            var isStgDebug=(window.location.href.match("stg_debug")||document.cookie.match("stg_debug"))&&!window.location.href.match("stg_disable_debug");
            stgCreateCookie("stg_debug",isStgDebug?1:"",isStgDebug?14:-1);
            var qP=[];dataLayerName!=="dataLayer"&&qP.push("data_layer_name="+dataLayerName),isStgDebug&&qP.push("stg_debug");
            !function(a,n,i){a[n]=a[n]||{};for(var c=0;c<i.length;c++)!function(i){a[n][i]=a[n][i]||{},a[n][i].api=a[n][i].api||function(){var a=[].slice.call(arguments,0);"string"==typeof a[0]&&window[dataLayerName].push({event:n+"."+i+":"+a[0],parameters:[].slice.call(arguments,1)})}}(i[c])}(window,"ppms",["tm","cm"]);
            })(window, document, 'dataLayer', '${piwikSiteId}');
        `;

        document.head.appendChild(configScript);
    }

    /**
     * To subscript to router events
     */
    private setupRouterEvents(): void {
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            combineLatestWith(this.piwikLoaded),
            filter(([_, piwikLoaded]) => piwikLoaded === true)
        ).subscribe(([event, _]) => {
            this.trackPageView((event as NavigationEnd).urlAfterRedirects);
        });
    }

    /**
     * To send page view data to piwik
     * @param url
     */
    private trackPageView(url: string): void {
        try {
            // Try to use ppms.tm.push which is the correct API call
            if ((window as any).ppms?.tm?.push) {
                (window as any).ppms.tm.push([
                    'trackPageView',
                    {
                        documentTitle: document.title,
                        href: url,
                    },
                ]);
            } else if ((window as any)._paq) {
                // Fallback to classic tracker if available
                const paq = (window as any)._paq;
                paq.push(['setDocumentTitle', document.title]);
                paq.push(['setCustomUrl', url]);
                paq.push(['trackPageView']);
            } else {
                console.warn('Neither Piwik Pro Tag Manager nor classic tracker available');
            }
        } catch (e) {
            console.error('Error tracking page view:', e);
        }
    }
}
